import {PayinStageBadge} from "../PayinParts";
import React from "react";
import type {
  MerchantOrderConversion,
  MerchantOrderItem,
  MerchantOrderStatus,
} from "../../api";
import {
  formatMerchantDate,
  merchantAmountText,
  merchantDirectionLabel,
  merchantOrderPartsLabel,
  merchantOrderStatusLabel,
  merchantStatusLabel,
  merchantStatusTone,
  merchantTermsLines,
} from "../../utils";

interface MerchantStatusBadgeProps {
  status: MerchantOrderStatus;
  label?: string;
}

export function MerchantStatusBadge({
  status,
  label,
}: MerchantStatusBadgeProps): JSX.Element {
  return (
    <span className={`merchant-status merchant-status-${merchantStatusTone(status)}`}>
      {label ?? merchantStatusLabel(status)}
    </span>
  );
}

function conversionAmountLabel(conversion: MerchantOrderConversion): string {
  if (conversion.amountMin) {
    // Сумма заявки ещё не выбрана: оценка дана вилкой, от нижней границы до верхней.
    return `≈ от ${conversion.amountMin.trim()} до ${conversion.amount.trim()} ${conversion.currency}`;
  }
  const amount = conversion.amount.trim();
  const signedAmount = amount.startsWith("-")
    ? `−${amount.slice(1)}`
    : amount.startsWith("+")
      ? amount
      : `+${amount}`;
  const prefix = conversion.kind === "estimated" ? "≈ " : "";
  return `${prefix}${signedAmount} ${conversion.currency}`;
}

function conversionRateLabel(conversion: MerchantOrderConversion): string {
  const prefix = conversion.kind === "estimated"
    ? "Зафиксированный курс"
    : "Фактический курс";
  return `${prefix}: ${conversion.rate} RUB/${conversion.currency}`;
}

export function MerchantConversion({
  conversion,
}: {
  conversion: MerchantOrderConversion;
}): JSX.Element {
  const rateLabel = conversionRateLabel(conversion);
  return (
    <span className="merchant-conversion">
      <span
        className={`balance-delta ${
          conversion.amount.trim().startsWith("-") ? "negative" : "positive"
        }`}
      >
        {conversionAmountLabel(conversion)}
      </span>
      <span className="merchant-conversion-rate">{rateLabel}</span>
    </span>
  );
}

/**
 * Условия входящей заявки мелким текстом под суммой: диапазон и переводы.
 * У заявки без условий не рисует ничего — ни пустой строки, ни отступа.
 */
export function MerchantTerms({item}: {item: MerchantOrderItem}): JSX.Element | null {
  const lines = merchantTermsLines(item);
  if (lines.length === 0) return null;
  return (
    <span className="merchant-terms">
      {lines.map((line) => (
        <span className="merchant-terms-line" key={line}>{line}</span>
      ))}
    </span>
  );
}

interface MerchantOrdersListProps {
  items: MerchantOrderItem[];
  emptyMessage: string;
  onDownloadReceipts?: (item: MerchantOrderItem) => void;
  downloadingReceiptOrderId?: string | null;
}

export function MerchantOrdersList({
  items,
  emptyMessage,
  onDownloadReceipts,
  downloadingReceiptOrderId = null,
}: MerchantOrdersListProps): JSX.Element {
  if (items.length === 0) {
    return <div className="empty-state compact">{emptyMessage}</div>;
  }

  return (
    <>
      <div className="table-wrapper merchant-orders-table-wrap">
        <table className="merchant-orders-table">
          <thead>
          <tr>
            <th>Заявка</th>
            <th>Направление</th>
            <th>Сумма</th>
            <th>Оплачено / осталось</th>
            <th>Статус</th>
            <th>Платежей</th>
            <th>Создана / обновлена</th>
            <th/>
          </tr>
          </thead>
          <tbody>
          {items.map((item) => (
            <tr key={item.orderId}>
              <td className="mono">
                <span className="merchant-remote-id">{item.remoteId}</span>
              </td>
              <td>
                <span className={`merchant-direction merchant-direction-${item.side}`}>
                  {merchantDirectionLabel(item.side)}
                </span>
              </td>
              <td className="mono">
                <span className="merchant-money-stack">
                  <span>{merchantAmountText(item)}</span>
                  <MerchantTerms item={item}/>
                  {item.conversion && (
                    <MerchantConversion conversion={item.conversion}/>
                  )}
                </span>
              </td>
              <td className="mono">
                <span className="merchant-money-stack">
                  <span>{item.paidAmount} {item.currency}</span>
                  <span className="muted">{item.remainingAmount} {item.currency}</span>
                </span>
              </td>
              <td>
                <MerchantStatusBadge
                  status={item.status}
                  label={merchantOrderStatusLabel(item)}
                />
                {item.payinStage && item.status !== "OK" && item.status !== "CANCEL" && <PayinStageBadge issued={item.payinStage === "requisites_issued"}/>}
              </td>
              <td className="mono">{merchantOrderPartsLabel(item)}</td>
              <td className="mono">
                <span className="merchant-date-stack">
                  <span>{formatMerchantDate(item.createdAt)}</span>
                  <span className="muted">{formatMerchantDate(item.updatedAt)}</span>
                </span>
              </td>
              <td>
                <div className="merchant-order-actions">
                  {onDownloadReceipts && item.receiptCount > 0 && (
                    <button
                      className="btn btn-ghost btn-sm"
                      type="button"
                      onClick={() => onDownloadReceipts(item)}
                      disabled={downloadingReceiptOrderId === item.orderId}
                    >
                      {downloadingReceiptOrderId === item.orderId
                        ? "Скачиваем…"
                        : item.receiptCount > 1
                          ? "Скачать чеки"
                          : "Скачать чек"}
                    </button>
                  )}
                  <a
                    className="btn btn-ghost btn-sm merchant-open-link"
                    href={`#/merchant/orders/${encodeURIComponent(item.orderId)}`}
                  >
                    Открыть
                  </a>
                </div>
              </td>
            </tr>
          ))}
          </tbody>
        </table>
      </div>

      <div className="merchant-order-cards">
        {items.map((item) => (
          <article className="merchant-order-card" key={item.orderId}>
            <div className="merchant-order-card-head">
              <div>
                <span className="meta-label">Заявка</span>
                <strong className="mono merchant-remote-id">{item.remoteId}</strong>
              </div>
              <MerchantStatusBadge
                status={item.status}
                label={merchantOrderStatusLabel(item)}
              />
              {item.payinStage && item.status !== "OK" && item.status !== "CANCEL" && <PayinStageBadge issued={item.payinStage === "requisites_issued"}/>}
            </div>
            <dl className="merchant-order-card-grid">
              <div>
                <dt>Направление</dt>
                <dd>{merchantDirectionLabel(item.side)}</dd>
              </div>
              <div>
                <dt>Сумма</dt>
                <dd className="merchant-money-stack mono">
                  <span>{merchantAmountText(item)}</span>
                  <MerchantTerms item={item}/>
                  {item.conversion && (
                    <MerchantConversion conversion={item.conversion}/>
                  )}
                </dd>
              </div>
              <div>
                <dt>Оплачено</dt>
                <dd className="mono">{item.paidAmount} {item.currency}</dd>
              </div>
              <div>
                <dt>Осталось</dt>
                <dd className="mono">{item.remainingAmount} {item.currency}</dd>
              </div>
              <div>
                <dt>Платежей</dt>
                <dd className="mono">{merchantOrderPartsLabel(item)}</dd>
              </div>
              <div>
                <dt>Обновлена</dt>
                <dd>{formatMerchantDate(item.updatedAt)}</dd>
              </div>
            </dl>
            <div className="merchant-order-card-actions">
              {onDownloadReceipts && item.receiptCount > 0 && (
                <button
                  className="btn btn-ghost btn-full"
                  type="button"
                  onClick={() => onDownloadReceipts(item)}
                  disabled={downloadingReceiptOrderId === item.orderId}
                >
                  {downloadingReceiptOrderId === item.orderId
                    ? "Скачиваем…"
                    : item.receiptCount > 1
                      ? "Скачать чеки"
                      : "Скачать чек"}
                </button>
              )}
              <a
                className="btn btn-ghost btn-full"
                href={`#/merchant/orders/${encodeURIComponent(item.orderId)}`}
              >
                Открыть детали
              </a>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}
